import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState, useMemo } from "react";
import { startOfDay, subDays, format, eachDayOfInterval, startOfToday, endOfDay, differenceInMinutes, differenceInDays, startOfWeek } from "date-fns";

export type DatePreset = "today" | "yesterday" | "7days" | "30days" | "custom";

export interface DateRange {
  from: Date;
  to: Date;
}

function getPresetRange(preset: DatePreset): DateRange {
  const today = startOfToday();
  switch (preset) {
    case "today":
      return { from: today, to: endOfDay(new Date()) };
    case "yesterday":
      return { from: subDays(today, 1), to: startOfDay(today) };
    case "7days":
      return { from: subDays(today, 6), to: endOfDay(new Date()) };
    case "30days":
      return { from: subDays(today, 29), to: endOfDay(new Date()) };
    default:
      return { from: subDays(today, 6), to: endOfDay(new Date()) };
  }
}

function getPrevRange(range: DateRange): DateRange {
  const durationMs = range.to.getTime() - range.from.getTime();
  return {
    from: new Date(range.from.getTime() - durationMs),
    to: new Date(range.from.getTime()),
  };
}

export function useDateRange() {
  const [preset, setPreset] = useState<DatePreset>("7days");
  const [customRange, setCustomRange] = useState<DateRange | null>(null);

  const range = useMemo(() => {
    if (preset === "custom" && customRange) {
      return { from: startOfDay(customRange.from), to: endOfDay(customRange.to) };
    }
    return getPresetRange(preset);
  }, [preset, customRange]);

  return { preset, setPreset, range, customRange, setCustomRange };
}

// Fetch all raw data
function useRawAnalyticsData(range: DateRange) {
  const fromISO = range.from.toISOString();
  const toISO = range.to.toISOString();
  const prevRange = getPrevRange(range);
  const prevFromISO = prevRange.from.toISOString();
  const prevToISO = prevRange.to.toISOString();

  const usersQuery = useQuery({
    queryKey: ["analytics-users", fromISO, toISO],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("telegram_users")
        .select("id, telegram_id, created_at, total_generations, first_discount_used, first_offer_shown_at, utm_source, utm_medium, utm_campaign, utm_content, last_purchase_at, wow_hits_balance")
        .gte("created_at", fromISO)
        .lte("created_at", toISO);
      if (error) throw error;
      return data || [];
    },
  });

  const prevUsersQuery = useQuery({
    queryKey: ["analytics-prev-users", prevFromISO, prevToISO],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("telegram_users")
        .select("id, total_generations, first_discount_used, first_offer_shown_at, utm_source, last_purchase_at")
        .gte("created_at", prevFromISO)
        .lte("created_at", prevToISO);
      if (error) throw error;
      return data || [];
    },
  });

  const allUsersQuery = useQuery({
    queryKey: ["analytics-all-users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("telegram_users")
        .select("id, telegram_id, created_at, total_generations, first_discount_used, utm_source, last_purchase_at, wow_hits_balance");
      if (error) throw error;
      return data || [];
    },
  });

  const generationsQuery = useQuery({
    queryKey: ["analytics-generations", fromISO, toISO],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("generations")
        .select("id, user_id, status, created_at, is_full_purchased, demo_url, full_price_rub")
        .gte("created_at", fromISO)
        .lte("created_at", toISO);
      if (error) throw error;
      return data || [];
    },
  });

  const prevGenerationsQuery = useQuery({
    queryKey: ["analytics-prev-generations", prevFromISO, prevToISO],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("generations")
        .select("id, user_id, status, created_at, demo_url")
        .gte("created_at", prevFromISO)
        .lte("created_at", prevToISO);
      if (error) throw error;
      return data || [];
    },
  });

  const transactionsQuery = useQuery({
    queryKey: ["analytics-transactions", fromISO, toISO],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transactions")
        .select("id, user_id, amount, price_rub, package_name, payment_id, status, created_at")
        .gte("created_at", fromISO)
        .lte("created_at", toISO);
      if (error) throw error;
      return data || [];
    },
  });

  const prevTransactionsQuery = useQuery({
    queryKey: ["analytics-prev-transactions", prevFromISO, prevToISO],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transactions")
        .select("id, user_id, amount, price_rub, package_name, status, created_at")
        .gte("created_at", prevFromISO)
        .lte("created_at", prevToISO);
      if (error) throw error;
      return data || [];
    },
  });

  const allTransactionsQuery = useQuery({
    queryKey: ["analytics-all-transactions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transactions")
        .select("id, user_id, amount, price_rub, package_name, status, created_at")
        .eq("status", "completed");
      if (error) throw error;
      return data || [];
    },
  });

  const allGenerationsQuery = useQuery({
    queryKey: ["analytics-all-generations-for-cohort"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("generations")
        .select("id, user_id, status, created_at, demo_url")
        .limit(1000);
      if (error) throw error;
      return data || [];
    },
  });

  const isLoading = usersQuery.isLoading || generationsQuery.isLoading || transactionsQuery.isLoading || allUsersQuery.isLoading || allTransactionsQuery.isLoading || prevUsersQuery.isLoading || prevGenerationsQuery.isLoading || prevTransactionsQuery.isLoading || allGenerationsQuery.isLoading;

  return {
    users: usersQuery.data || [],
    prevUsers: prevUsersQuery.data || [],
    allUsers: allUsersQuery.data || [],
    generations: generationsQuery.data || [],
    prevGenerations: prevGenerationsQuery.data || [],
    transactions: transactionsQuery.data || [],
    prevTransactions: prevTransactionsQuery.data || [],
    allTransactions: allTransactionsQuery.data || [],
    allGenerations: allGenerationsQuery.data || [],
    isLoading,
  };
}

// --- Types ---
export interface SummaryMetrics {
  uniqueUsers: number;
  startedCreation: number;
  gotDemo: number;
  paid: number;
  totalRevenue: number;
  avgCheck: number;
  repeatPurchasePercent: number;
}

export interface FunnelStep {
  label: string;
  value: number;
  percent: number;
}

export interface PackageRow {
  name: string;
  sales: number;
  revenue: number;
  percent: number;
  repeatRate: number;
}

export interface UtmRow {
  source: string;
  users: number;
  demos: number;
  purchases: number;
  revenue: number;
  conversion: number;
  avgCheck: number;
}

export interface DailyDataPoint {
  date: string;
  revenue: number;
  purchases: number;
  demos: number;
  conversion: number;
}

export interface ConversionKPI {
  current: number;
  today: number;
  week7: number;
  week30: number;
  prevPeriod: number;
  delta: number;
}

export interface TimeToPurchase {
  avgMinutes: number;
  medianMinutes: number;
  within5min: number;
  after10min: number;
}

export interface SpecialOffer99 {
  shown: number;
  bought: number;
  conversion: number;
  repeatPurchase: number;
  sales: number;
}

export interface CohortRow {
  week: string;
  newUsers: number;
  purchaseDay0: number;
  purchaseDay7: number;
  purchaseDay30: number;
  ltv: number;
}

export interface FunnelLoss {
  fromStep: string;
  toStep: string;
  absoluteDrop: number;
  percentLoss: number;
}

export interface PartyAnalysis {
  totalBuyers: number;
  avgHitsCreated: number;
  pct8plus: number;
  pctCompleted: number;
}

export interface PeriodComparison {
  label: string;
  current: number;
  previous: number;
  delta: number;
  isNegative: boolean;
}

export function useAnalyticsDashboard(range: DateRange) {
  const raw = useRawAnalyticsData(range);

  const computed = useMemo(() => {
    if (raw.isLoading) return null;

    const completedTx = raw.transactions.filter(t => t.status === "completed");
    const prevCompletedTx = raw.prevTransactions.filter(t => t.status === "completed");
    const allTxByUser: Record<string, number> = {};
    raw.allTransactions.forEach(t => {
      allTxByUser[t.user_id] = (allTxByUser[t.user_id] || 0) + 1;
    });
    const totalBuyers = Object.keys(allTxByUser).length;
    const repeatBuyers = Object.values(allTxByUser).filter(c => c > 1).length;

    // --- Summary ---
    const uniqueUsers = raw.users.length;
    const startedCreation = raw.generations.length;
    const gotDemo = raw.generations.filter(g => g.demo_url || g.status === "completed").length;
    const paid = completedTx.length;
    const totalRevenue = completedTx.reduce((s, t) => s + t.price_rub, 0);
    const avgCheck = paid > 0 ? Math.round(totalRevenue / paid) : 0;
    const repeatPurchasePercent = totalBuyers > 0 ? Math.round((repeatBuyers / totalBuyers) * 100) : 0;

    const summary: SummaryMetrics = {
      uniqueUsers, startedCreation, gotDemo, paid, totalRevenue, avgCheck, repeatPurchasePercent,
    };

    // --- 1. Conversion KPI ---
    const currentConv = gotDemo > 0 ? Math.round((paid / gotDemo) * 100) : 0;

    const todayStart = startOfToday();
    const todayEnd = endOfDay(new Date());
    const todayGens = raw.generations.filter(g => { const d = new Date(g.created_at); return d >= todayStart && d <= todayEnd; });
    const todayDemos = todayGens.filter(g => g.demo_url || g.status === "completed").length;
    const todayTx = completedTx.filter(t => { const d = new Date(t.created_at); return d >= todayStart && d <= todayEnd; });
    const todayConv = todayDemos > 0 ? Math.round((todayTx.length / todayDemos) * 100) : 0;

    // 7-day and 30-day conversion from all data
    const d7 = subDays(new Date(), 6);
    const d30 = subDays(new Date(), 29);
    const gens7 = raw.allGenerations.filter(g => new Date(g.created_at) >= d7);
    const demos7 = gens7.filter(g => g.demo_url || g.status === "completed").length;
    const tx7 = raw.allTransactions.filter(t => new Date(t.created_at) >= d7).length;
    const conv7 = demos7 > 0 ? Math.round((tx7 / demos7) * 100) : 0;
    const gens30 = raw.allGenerations.filter(g => new Date(g.created_at) >= d30);
    const demos30 = gens30.filter(g => g.demo_url || g.status === "completed").length;
    const tx30 = raw.allTransactions.filter(t => new Date(t.created_at) >= d30).length;
    const conv30 = demos30 > 0 ? Math.round((tx30 / demos30) * 100) : 0;

    const prevDemos = raw.prevGenerations.filter(g => g.demo_url || g.status === "completed").length;
    const prevConv = prevDemos > 0 ? Math.round((prevCompletedTx.length / prevDemos) * 100) : 0;

    const conversionKPI: ConversionKPI = {
      current: currentConv,
      today: todayConv,
      week7: conv7,
      week30: conv30,
      prevPeriod: prevConv,
      delta: currentConv - prevConv,
    };

    // --- 2. Time to purchase ---
    // For each user with a purchase, find their first demo and first purchase
    const userFirstDemo: Record<string, Date> = {};
    raw.allGenerations.forEach(g => {
      if (g.demo_url || g.status === "completed") {
        const d = new Date(g.created_at);
        if (!userFirstDemo[g.user_id] || d < userFirstDemo[g.user_id]) {
          userFirstDemo[g.user_id] = d;
        }
      }
    });
    const userFirstPurchase: Record<string, Date> = {};
    raw.allTransactions.forEach(t => {
      const d = new Date(t.created_at);
      if (!userFirstPurchase[t.user_id] || d < userFirstPurchase[t.user_id]) {
        userFirstPurchase[t.user_id] = d;
      }
    });
    const timeDiffs: number[] = [];
    Object.keys(userFirstPurchase).forEach(uid => {
      if (userFirstDemo[uid]) {
        const diff = differenceInMinutes(userFirstPurchase[uid], userFirstDemo[uid]);
        if (diff >= 0) timeDiffs.push(diff);
      }
    });
    timeDiffs.sort((a, b) => a - b);
    const avgMinutes = timeDiffs.length > 0 ? Math.round(timeDiffs.reduce((s, v) => s + v, 0) / timeDiffs.length) : 0;
    const medianMinutes = timeDiffs.length > 0 ? timeDiffs[Math.floor(timeDiffs.length / 2)] : 0;
    const within5min = timeDiffs.length > 0 ? Math.round((timeDiffs.filter(d => d <= 5).length / timeDiffs.length) * 100) : 0;
    const after10min = timeDiffs.length > 0 ? Math.round((timeDiffs.filter(d => d > 10).length / timeDiffs.length) * 100) : 0;

    const timeToPurchase: TimeToPurchase = { avgMinutes, medianMinutes, within5min, after10min };

    // --- 3. Special offer 99₽ ---
    const shownOffer = raw.users.filter(u => u.first_offer_shown_at).length;
    const bought99 = completedTx.filter(t => t.price_rub === 99);
    const bought99Users = new Set(bought99.map(t => t.user_id));
    const bought99Repeat = [...bought99Users].filter(uid => (allTxByUser[uid] || 0) > 1).length;
    const specialOffer99: SpecialOffer99 = {
      shown: shownOffer,
      bought: bought99Users.size,
      conversion: shownOffer > 0 ? Math.round((bought99Users.size / shownOffer) * 100) : 0,
      repeatPurchase: bought99Users.size > 0 ? Math.round((bought99Repeat / bought99Users.size) * 100) : 0,
      sales: bought99.length,
    };

    // --- 4. Cohort analysis (by week) ---
    const cohortMap: Record<string, { users: Set<string>; purchaseDay0: Set<string>; purchaseDay7: Set<string>; purchaseDay30: Set<string>; revenue: number }> = {};
    raw.allUsers.forEach(u => {
      const weekStart = format(startOfWeek(new Date(u.created_at), { weekStartsOn: 1 }), "dd.MM");
      if (!cohortMap[weekStart]) cohortMap[weekStart] = { users: new Set(), purchaseDay0: new Set(), purchaseDay7: new Set(), purchaseDay30: new Set(), revenue: 0 };
      cohortMap[weekStart].users.add(u.id);
    });
    raw.allTransactions.forEach(t => {
      const user = raw.allUsers.find(u => u.id === t.user_id);
      if (!user) return;
      const weekStart = format(startOfWeek(new Date(user.created_at), { weekStartsOn: 1 }), "dd.MM");
      if (!cohortMap[weekStart]) return;
      const daysDiff = differenceInDays(new Date(t.created_at), new Date(user.created_at));
      if (daysDiff <= 1) cohortMap[weekStart].purchaseDay0.add(t.user_id);
      if (daysDiff <= 7) cohortMap[weekStart].purchaseDay7.add(t.user_id);
      if (daysDiff <= 30) cohortMap[weekStart].purchaseDay30.add(t.user_id);
      cohortMap[weekStart].revenue += t.price_rub;
    });
    const cohorts: CohortRow[] = Object.entries(cohortMap)
      .map(([week, d]) => ({
        week,
        newUsers: d.users.size,
        purchaseDay0: d.purchaseDay0.size,
        purchaseDay7: d.purchaseDay7.size,
        purchaseDay30: d.purchaseDay30.size,
        ltv: d.users.size > 0 ? Math.round(d.revenue / d.users.size) : 0,
      }))
      .slice(-8); // Last 8 weeks

    // --- 5. Avg hits per paying user ---
    const payingUserIds = new Set(raw.allTransactions.map(t => t.user_id));
    const payingUsersData = raw.allUsers.filter(u => payingUserIds.has(u.id));
    const avgHitsPerPayer = payingUsersData.length > 0 
      ? +(payingUsersData.reduce((s, u) => s + u.total_generations, 0) / payingUsersData.length).toFixed(1) 
      : 0;

    // --- 6. Funnel loss ---
    const funnelSteps = [
      { label: "Новые пользователи", value: uniqueUsers },
      { label: "Начали создание", value: startedCreation },
      { label: "Получили демо", value: gotDemo },
      { label: "Оплатили", value: paid },
    ];
    let biggestLoss: FunnelLoss = { fromStep: "", toStep: "", absoluteDrop: 0, percentLoss: 0 };
    for (let i = 0; i < funnelSteps.length - 1; i++) {
      const from = funnelSteps[i];
      const to = funnelSteps[i + 1];
      if (from.value > 0) {
        const loss = Math.round(((from.value - to.value) / from.value) * 100);
        if (loss > biggestLoss.percentLoss) {
          biggestLoss = {
            fromStep: from.label,
            toStep: to.label,
            absoluteDrop: from.value - to.value,
            percentLoss: loss,
          };
        }
      }
    }

    // --- Funnel ---
    const funnel: FunnelStep[] = funnelSteps.map((s, i) => ({
      label: s.label,
      value: s.value,
      percent: i === 0 ? 100 : (funnelSteps[i - 1].value > 0 ? Math.round((s.value / funnelSteps[i - 1].value) * 100) : 0),
    }));

    // --- 7. UTM with demos ---
    const utmMap: Record<string, { users: Set<string>; demos: number; purchases: number; revenue: number }> = {};
    raw.users.forEach(u => {
      const src = u.utm_source || "direct";
      if (!utmMap[src]) utmMap[src] = { users: new Set(), demos: 0, purchases: 0, revenue: 0 };
      utmMap[src].users.add(u.id);
    });
    raw.generations.filter(g => g.demo_url || g.status === "completed").forEach(g => {
      const user = raw.users.find(u => u.id === g.user_id);
      const src = user?.utm_source || "direct";
      if (!utmMap[src]) utmMap[src] = { users: new Set(), demos: 0, purchases: 0, revenue: 0 };
      utmMap[src].demos++;
    });
    completedTx.forEach(t => {
      const user = raw.users.find(u => u.id === t.user_id);
      const src = user?.utm_source || "direct";
      if (!utmMap[src]) utmMap[src] = { users: new Set(), demos: 0, purchases: 0, revenue: 0 };
      utmMap[src].purchases++;
      utmMap[src].revenue += t.price_rub;
    });
    const utmRows: UtmRow[] = Object.entries(utmMap)
      .map(([source, d]) => ({
        source,
        users: d.users.size,
        demos: d.demos,
        purchases: d.purchases,
        revenue: d.revenue,
        conversion: d.users.size > 0 ? Math.round((d.purchases / d.users.size) * 100) : 0,
        avgCheck: d.purchases > 0 ? Math.round(d.revenue / d.purchases) : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue);

    // --- 9. Packages with repeat rate ---
    const packageMap: Record<string, { sales: number; revenue: number; buyers: Set<string> }> = {};
    completedTx.forEach(t => {
      if (!packageMap[t.package_name]) packageMap[t.package_name] = { sales: 0, revenue: 0, buyers: new Set() };
      packageMap[t.package_name].sales++;
      packageMap[t.package_name].revenue += t.price_rub;
      packageMap[t.package_name].buyers.add(t.user_id);
    });
    const packages: PackageRow[] = Object.entries(packageMap)
      .map(([name, d]) => ({
        name,
        sales: d.sales,
        revenue: d.revenue,
        percent: paid > 0 ? Math.round((d.sales / paid) * 100) : 0,
        repeatRate: d.buyers.size > 0 ? Math.round(([...d.buyers].filter(uid => (allTxByUser[uid] || 0) > 1).length / d.buyers.size) * 100) : 0,
      }))
      .sort((a, b) => b.revenue - a.revenue);

    // --- 10. Party analysis (15 hits packages) ---
    const partyTx = raw.allTransactions.filter(t => t.amount >= 10);
    const partyBuyers = new Set(partyTx.map(t => t.user_id));
    const partyUsersData = raw.allUsers.filter(u => partyBuyers.has(u.id));
    const partyAnalysis: PartyAnalysis = {
      totalBuyers: partyBuyers.size,
      avgHitsCreated: partyUsersData.length > 0 ? +(partyUsersData.reduce((s, u) => s + u.total_generations, 0) / partyUsersData.length).toFixed(1) : 0,
      pct8plus: partyUsersData.length > 0 ? Math.round((partyUsersData.filter(u => u.total_generations >= 8).length / partyUsersData.length) * 100) : 0,
      pctCompleted: partyUsersData.length > 0 ? Math.round((partyUsersData.filter(u => u.total_generations >= 15).length / partyUsersData.length) * 100) : 0,
    };

    // --- New user behavior ---
    const newUsersWithGen = raw.users.filter(u => u.total_generations > 0).length;
    const newUsersShownOffer = raw.users.filter(u => u.first_offer_shown_at).length;
    const newUsersBought99 = completedTx.filter(t => raw.users.some(u => u.id === t.user_id) && t.price_rub === 99).length;
    const newUsersBoughtRepeat = [...new Set(completedTx.filter(t => raw.users.some(u => u.id === t.user_id)).map(t => t.user_id))]
      .filter(uid => (allTxByUser[uid] || 0) > 1).length;
    const newUserBehavior = {
      total: uniqueUsers,
      usedTrial: newUsersWithGen,
      shownOffer: newUsersShownOffer,
      bought99: newUsersBought99,
      repeatPurchase: newUsersBoughtRepeat,
    };

    // --- Period comparison ---
    const prevUniqueUsers = raw.prevUsers.length;
    const prevGotDemo = raw.prevGenerations.filter(g => g.demo_url || g.status === "completed").length;
    const prevPaid = prevCompletedTx.length;
    const prevRevenue = prevCompletedTx.reduce((s, t) => s + t.price_rub, 0);
    const prevAvgCheck = prevPaid > 0 ? Math.round(prevRevenue / prevPaid) : 0;

    const calcDelta = (curr: number, prev: number) => prev > 0 ? Math.round(((curr - prev) / prev) * 100) : (curr > 0 ? 100 : 0);
    const periodComparison: PeriodComparison[] = [
      { label: "Пользователи", current: uniqueUsers, previous: prevUniqueUsers, delta: calcDelta(uniqueUsers, prevUniqueUsers), isNegative: uniqueUsers < prevUniqueUsers },
      { label: "Демо", current: gotDemo, previous: prevGotDemo, delta: calcDelta(gotDemo, prevGotDemo), isNegative: gotDemo < prevGotDemo },
      { label: "Покупки", current: paid, previous: prevPaid, delta: calcDelta(paid, prevPaid), isNegative: paid < prevPaid },
      { label: "Выручка", current: totalRevenue, previous: prevRevenue, delta: calcDelta(totalRevenue, prevRevenue), isNegative: totalRevenue < prevRevenue },
      { label: "Ср. чек", current: avgCheck, previous: prevAvgCheck, delta: calcDelta(avgCheck, prevAvgCheck), isNegative: avgCheck < prevAvgCheck },
    ];

    // --- LTV ---
    const allUsersCount = raw.allUsers.length;
    const avgHitsPerUser = allUsersCount > 0 
      ? +(raw.allUsers.reduce((s, u) => s + u.total_generations, 0) / allUsersCount).toFixed(1) 
      : 0;
    const totalAllRevenue = raw.allTransactions.reduce((s, t) => s + t.price_rub, 0);
    const ltv = allUsersCount > 0 ? Math.round(totalAllRevenue / allUsersCount) : 0;
    const repeatBuyerShare = totalBuyers > 0 ? Math.round((repeatBuyers / totalBuyers) * 100) : 0;

    // --- Daily charts ---
    const days = eachDayOfInterval({ start: range.from, end: range.to > new Date() ? new Date() : range.to });
    const dailyData: DailyDataPoint[] = days.map(day => {
      const dayStart = startOfDay(day);
      const dayEnd = endOfDay(day);
      const dayTx = completedTx.filter(t => { const d = new Date(t.created_at); return d >= dayStart && d <= dayEnd; });
      const dayGens = raw.generations.filter(g => { const d = new Date(g.created_at); return d >= dayStart && d <= dayEnd; });
      const dayDemos = dayGens.filter(g => g.demo_url || g.status === "completed").length;
      const dayPurchases = dayTx.length;
      const dayRevenue = dayTx.reduce((s, t) => s + t.price_rub, 0);
      return {
        date: format(day, "dd.MM"),
        revenue: dayRevenue,
        purchases: dayPurchases,
        demos: dayDemos,
        conversion: dayDemos > 0 ? Math.round((dayPurchases / dayDemos) * 100) : 0,
      };
    });

    return {
      summary,
      conversionKPI,
      timeToPurchase,
      specialOffer99,
      cohorts,
      avgHitsPerPayer,
      funnelLoss: biggestLoss,
      funnel,
      packages,
      ltv: { avgCheck, avgHitsPerUser, ltv, repeatBuyerShare },
      utmRows,
      partyAnalysis,
      newUserBehavior,
      periodComparison,
      dailyData,
    };
  }, [raw, range]);

  return { data: computed, isLoading: raw.isLoading };
}
